import { motion } from "framer-motion";
import { Eyebrow } from "@/components/ui-custom/Eyebrow";
import { NJ_VIEWBOX, NJ_COUNTIES } from "@/data/njCounties";

const PRIMARY = ["Monmouth"];
const SECONDARY = ["Union", "Middlesex", "Ocean"];

const GOLD = "#c9a24b";
const FILL_PRIMARY = "rgba(201,162,75,0.62)";
const FILL_SECONDARY = "rgba(201,162,75,0.24)";
const FILL_BASE = "#efe9db";
const STROKE = "#ffffff";

const tierOf = (name) => (PRIMARY.includes(name) ? "primary" : SECONDARY.includes(name) ? "secondary" : "base");
const fillOf = (name) => {
  const t = tierOf(name);
  return t === "primary" ? FILL_PRIMARY : t === "secondary" ? FILL_SECONDARY : FILL_BASE;
};

// Colts Neck (AIM HQ) — western Monmouth, in viewBox units (viewBox width 315, height 560)
const PIN = { x: 214, y: 258 };

const MapCanvas = () => (
  <div className="relative w-full" data-testid="nj-map-canvas">
    <svg viewBox={NJ_VIEWBOX} width="100%" height="100%" style={{ display: "block" }} role="img" aria-label="AIM High New Jersey service-area map">
      <g>
        {NJ_COUNTIES.map((c) => (
          <motion.path
            key={c.name}
            d={c.d}
            fill={fillOf(c.name)}
            stroke={STROKE}
            strokeWidth={0.9}
            strokeLinejoin="round"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid={`nj-county-${c.name.toLowerCase()}`}
          />
        ))}
      </g>
      <g>
        {NJ_COUNTIES.map((c) => {
          const served = tierOf(c.name) !== "base";
          return (
            <text
              key={c.name}
              x={c.lx}
              y={c.ly}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: served ? 8.2 : 7.4,
                fontWeight: served ? 700 : 500,
                letterSpacing: "0.02em",
                fill: served ? "#5a4416" : "#9a938260",
                textTransform: "uppercase",
                pointerEvents: "none",
              }}
            >
              {c.name.toUpperCase()}
            </text>
          );
        })}
      </g>
      {/* HQ pin */}
      <g>
        <motion.circle
          cx={PIN.x}
          cy={PIN.y}
          r={9}
          fill={GOLD}
          opacity={0.25}
          initial={{ scale: 0 }}
          whileInView={{ scale: [0.6, 1.15, 0.85, 1] }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, delay: 0.5 }}
          style={{ transformOrigin: `${PIN.x}px ${PIN.y}px` }}
        />
        <circle cx={PIN.x} cy={PIN.y} r={4} fill={GOLD} stroke="#fff" strokeWidth={1.4} data-testid="nj-hq-pin" />
      </g>
    </svg>

    {/* Compass */}
    <div className="absolute" style={{ top: "4%", left: "6%" }} aria-hidden="true">
      <div className="flex flex-col items-center" style={{ color: GOLD }}>
        <span style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", marginBottom: 2 }}>N</span>
        <svg width="30" height="30" viewBox="0 0 30 30">
          <circle cx="15" cy="15" r="13" fill="none" stroke={GOLD} strokeWidth="1" opacity="0.6" />
          <path d="M15 5 L18 16 L15 13 L12 16 Z" fill={GOLD} />
        </svg>
      </div>
    </div>

    {/* HQ callout label */}
    <div className="absolute" style={{ right: "3%", top: "38%", maxWidth: "48%" }} data-testid="nj-hq-callout">
      <div style={{ background: "#fff", border: `1px solid ${GOLD}`, borderRadius: 6, padding: "6px 10px", boxShadow: "0 8px 20px rgba(0,0,0,0.12)", whiteSpace: "nowrap" }}>
        <div style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 12, letterSpacing: "0.14em", color: "var(--text-dark)" }}>COLTS NECK</div>
        <div style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 9, letterSpacing: "0.1em", color: GOLD, marginTop: 1 }}>AIM HIGH</div>
      </div>
    </div>

    {/* Legend */}
    <div className="absolute" style={{ bottom: "6%", right: "5%" }} data-testid="nj-map-legend">
      <div style={{ background: "#fff", border: "1px solid #e2ddd1", borderRadius: 8, padding: "10px 12px", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" }}>
        <div className="flex items-center gap-2.5" style={{ marginBottom: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: FILL_PRIMARY, display: "inline-block" }} />
          <span style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 10.5, letterSpacing: "0.08em", color: "var(--text-dark)", textTransform: "uppercase" }}>Primary Service Area</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span style={{ width: 14, height: 14, borderRadius: 4, background: FILL_SECONDARY, display: "inline-block" }} />
          <span style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 10.5, letterSpacing: "0.08em", color: "var(--text-dark)", textTransform: "uppercase" }}>Extended Service Area</span>
        </div>
      </div>
    </div>
  </div>
);

export const NJServiceMap = () => {
  return (
    <section className="sec-offwhite section-y" data-testid="nj-service-map">
      <div className="container-1200 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Eyebrow start>Where We Work</Eyebrow>
          <h2 className="h2 mt-6" style={{ lineHeight: 1.05 }}>
            Local hands.
            <br />
            <span style={{ color: "var(--cta-deep, #a9822f)" }}>Deep Jersey roots.</span>
          </h2>
          <p className="lead mt-7" style={{ color: "var(--text-dark)", maxWidth: "44ch", fontSize: 18 }}>
            <strong>Primarily serving Monmouth County</strong> — with trusted reach into Union, Middlesex &amp; Ocean. Roofing, siding, kitchens, baths, basements and commercial work across Colts Neck, Holmdel, Marlboro, Freehold, Middletown, Manalapan, Rumson, Red Bank, Wall, Howell, and every town in between.
          </p>
          <div className="mt-8 flex flex-col gap-3" data-testid="nj-map-legend-inline">
            <span className="flex items-center gap-2.5" style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 13, color: "var(--text-dark)" }}>
              <span style={{ width: 16, height: 16, borderRadius: 5, background: FILL_PRIMARY, display: "inline-block" }} />
              Primary · Monmouth
            </span>
            <span className="flex items-center gap-2.5" style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 13, color: "var(--text-dark)" }}>
              <span style={{ width: 16, height: 16, borderRadius: 5, background: FILL_SECONDARY, display: "inline-block" }} />
              Extended · Union · Middlesex · Ocean
            </span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div style={{ background: "#fff", border: "1px solid #e6e0d3", borderRadius: 20, boxShadow: "0 28px 60px rgba(23,23,26,0.10)", padding: "22px", overflow: "hidden" }}>
            <div className="mx-auto" style={{ maxWidth: 460 }}>
              <MapCanvas />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
